import { FC, useEffect, useRef } from 'react';
import * as THREE from 'three';

/**
 * Fondo decorativo de las vistas sin sesión: hojas de contrato suspendidas en
 * un espacio 3D con una constelación de nodos (el «hilo conductor» de la
 * marca).
 *
 * Estático: la escena se compone una vez y se pinta un solo fotograma, que
 * sólo se repinta si la ventana cambia de tamaño. Sin bucle de render, sin
 * flotado y sin parallax de cursor. Con `prefers-reduced-motion` el
 * comportamiento es el mismo de siempre: un fotograma quieto; sin WebGL no
 * se monta nada y el lienzo cae sobre los resplandores y las líneas SVG que
 * dibuja el layout. Todo lo que crea el efecto se libera al desmontar.
 */
const AuthBackground: FC = () => {
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    // Sin WebGL no hay escena que mostrar.
    const probe = document.createElement('canvas');
    const gl = probe.getContext('webgl2') || probe.getContext('webgl');
    if (!gl) return;

    const width = () => mount.clientWidth || window.innerWidth;
    const height = () => mount.clientHeight || window.innerHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, width() / height(), 0.1, 1000);
    camera.position.set(0, 0, 18);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width(), height());
    mount.appendChild(renderer.domElement);

    // Iluminación del espacio: ambiente profundo más dos puntuales témal y
    // esmeralda. Sin decaimiento (decay 0) para conservar el lavado de color
    // amplio del diseño original.
    scene.add(new THREE.AmbientLight(0x0a2f2b, 1.8));

    const lightTeal = new THREE.PointLight(0x2dd4bf, 3.5, 50, 0);
    lightTeal.position.set(-8, 6, 10);
    scene.add(lightTeal);

    const lightEmerald = new THREE.PointLight(0x10b981, 2.8, 50, 0);
    lightEmerald.position.set(8, -5, 8);
    scene.add(lightEmerald);

    /** Textura procedural de canvas: una hoja de contrato con encabezado,
     * cláusulas, tabla y áreas de firma. `typeIndex` alterna el tinte. */
    const createDocumentTexture = (typeIndex: number) => {
      const canvas = document.createElement('canvas');
      canvas.width = 512;
      canvas.height = 700;
      const ctx = canvas.getContext('2d')!;

      ctx.fillStyle = 'rgba(10, 32, 28, 0.85)';
      ctx.fillRect(0, 0, 512, 700);

      ctx.strokeStyle = typeIndex % 2 === 0 ? 'rgba(45, 212, 191, 0.7)' : 'rgba(16, 185, 129, 0.6)';
      ctx.lineWidth = 6;
      ctx.strokeRect(6, 6, 500, 688);

      // Solapa de la esquina.
      ctx.fillStyle = typeIndex % 2 === 0 ? '#14b8a6' : '#059669';
      ctx.beginPath();
      ctx.moveTo(420, 0);
      ctx.lineTo(512, 92);
      ctx.lineTo(420, 92);
      ctx.closePath();
      ctx.fill();

      // Encabezado y punto de estado.
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.fillRect(40, 48, 160, 20);
      ctx.fillStyle = typeIndex % 2 === 0 ? '#2dd4bf' : '#34d399';
      ctx.beginPath();
      ctx.arc(380, 58, 10, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = 'rgba(45, 212, 191, 0.4)';
      ctx.fillRect(40, 90, 432, 3);

      const lineConfigs = [
        { y: 130, w: 320, h: 12, alpha: 0.65 },
        { y: 155, w: 400, h: 10, alpha: 0.35 },
        { y: 175, w: 360, h: 10, alpha: 0.35 },
        { y: 195, w: 280, h: 10, alpha: 0.35 },
        { y: 240, w: 432, h: 90, alpha: 0.15, isBox: true },
        { y: 360, w: 380, h: 10, alpha: 0.4 },
        { y: 380, w: 340, h: 10, alpha: 0.35 },
        { y: 400, w: 390, h: 10, alpha: 0.35 },
        { y: 420, w: 220, h: 10, alpha: 0.35 },
        { y: 470, w: 180, h: 48, alpha: 0.25, isStamp: true },
        { y: 470, x: 260, w: 210, h: 48, alpha: 0.25, isStamp: true },
        { y: 550, w: 432, h: 2, alpha: 0.3 },
        { y: 580, w: 140, h: 12, alpha: 0.5 },
      ];

      lineConfigs.forEach((item) => {
        if (item.isBox) {
          ctx.strokeStyle = 'rgba(45, 212, 191, 0.4)';
          ctx.lineWidth = 2;
          ctx.strokeRect(40, item.y, item.w, item.h);
          ctx.fillStyle = 'rgba(20, 184, 166, 0.08)';
          ctx.fillRect(40, item.y, item.w, item.h);
          ctx.fillStyle = 'rgba(45, 212, 191, 0.25)';
          ctx.fillRect(40, item.y + 30, item.w, 2);
        } else if (item.isStamp) {
          ctx.strokeStyle = 'rgba(45, 212, 191, 0.5)';
          ctx.lineWidth = 1.5;
          ctx.strokeRect(item.x || 40, item.y, item.w, item.h);
          ctx.strokeStyle = '#2dd4bf';
          ctx.beginPath();
          const startX = (item.x || 40) + 20;
          ctx.moveTo(startX, item.y + 30);
          ctx.bezierCurveTo(startX + 30, item.y + 10, startX + 60, item.y + 40, startX + 100, item.y + 20);
          ctx.bezierCurveTo(startX + 120, item.y + 15, startX + 140, item.y + 35, startX + 150, item.y + 25);
          ctx.stroke();
        } else {
          ctx.fillStyle = `rgba(255, 255, 255, ${item.alpha})`;
          ctx.fillRect(item.x || 40, item.y, item.w, item.h);
        }
      });

      const texture = new THREE.CanvasTexture(canvas);
      texture.minFilter = THREE.LinearFilter;
      texture.colorSpace = THREE.SRGBColorSpace;
      return texture;
    };

    // Grupo de hojas: una sola geometría combada (papel flexible) compartida.
    const docGroup = new THREE.Group();
    scene.add(docGroup);

    const docGeom = new THREE.PlaneGeometry(2.6, 3.56, 16, 16);
    const pos = docGeom.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      pos.setZ(i, Math.sin(x * 1.2) * 0.08 + Math.cos(y * 0.9) * 0.05);
    }
    docGeom.computeVertexNormals();

    const documents: Array<{
      mesh: THREE.Mesh;
      material: THREE.MeshPhongMaterial;
      floatAmpY: number;
      floatFreq: number;
      rotSpeedX: number;
      rotSpeedY: number;
      basePos: THREE.Vector3;
      phase: number;
    }> = [];

    for (let i = 0; i < 14; i++) {
      const material = new THREE.MeshPhongMaterial({
        map: createDocumentTexture(i),
        transparent: true,
        opacity: 0.72,
        side: THREE.DoubleSide,
        shininess: 60,
        specular: new THREE.Color(0x2dd4bf),
        depthWrite: false,
      });
      const mesh = new THREE.Mesh(docGeom, material);
      mesh.position.set(
        (Math.random() - 0.5) * 26,
        (Math.random() - 0.5) * 16,
        (Math.random() - 0.5) * 10 - 2,
      );
      mesh.rotation.x = (Math.random() - 0.5) * 0.7;
      mesh.rotation.y = (Math.random() - 0.5) * 1.0;
      mesh.rotation.z = (Math.random() - 0.5) * 0.6;

      documents.push({
        mesh,
        material,
        floatAmpY: 0.8 + Math.random() * 0.8,
        floatFreq: 0.6 + Math.random() * 0.8,
        rotSpeedX: (Math.random() - 0.5) * 0.004,
        rotSpeedY: (Math.random() - 0.5) * 0.006,
        basePos: mesh.position.clone(),
        phase: Math.random() * Math.PI * 2,
      });
      docGroup.add(mesh);
    }

    // Constelación de nodos de datos que enlaza los documentos.
    const particleCount = 70;
    const particleGeom = new THREE.BufferGeometry();
    const particlePositions = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount * 3; i += 3) {
      particlePositions[i] = (Math.random() - 0.5) * 28;
      particlePositions[i + 1] = (Math.random() - 0.5) * 18;
      particlePositions[i + 2] = (Math.random() - 0.5) * 12;
    }
    particleGeom.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));
    const particleMat = new THREE.PointsMaterial({
      color: 0x34d399,
      size: 0.18,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending,
    });
    const particles = new THREE.Points(particleGeom, particleMat);
    scene.add(particles);

    // Fondo estático para todos: se compone una vez y se pinta UN fotograma
    // (repintado sólo si la ventana cambia de tamaño). Sin bucle de render,
    // sin flotado de hojas y sin parallax de cursor: el aspecto de la
    // composición, sin movimiento y sin costo de GPU por fotograma.
    const onResize = () => {
      camera.aspect = width() / height();
      camera.updateProjectionMatrix();
      renderer.setSize(width(), height());
      renderer.render(scene, camera);
    };
    window.addEventListener('resize', onResize);

    renderer.render(scene, camera);

    return () => {
      window.removeEventListener('resize', onResize);
      documents.forEach((doc) => {
        doc.material.map?.dispose();
        doc.material.dispose();
      });
      docGeom.dispose();
      particleGeom.dispose();
      particleMat.dispose();
      renderer.dispose();
      mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className="auth__bg" aria-hidden="true" />;
};

export default AuthBackground;
